import { Temporal } from '@js-temporal/polyfill'

// Display and report boundaries use Tehran, independently of the device timezone.
export const CHEF_TIME_ZONE = 'Asia/Tehran'
export type DateInput = number | string | Date | null | undefined

export type CalendarDate = { year: number; month: number; day: number }
const persian = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
  timeZone: CHEF_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
})
const weekday = new Intl.DateTimeFormat('fa-IR', { timeZone: CHEF_TIME_ZONE, weekday: 'long' })
const persianDayMonth = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
  timeZone: CHEF_TIME_ZONE, day: 'numeric', month: 'long', year: 'numeric',
})
const persianMonthDay = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
  timeZone: CHEF_TIME_ZONE, day: 'numeric', month: 'long',
})
const time = new Intl.DateTimeFormat('fa-IR', {
  timeZone: CHEF_TIME_ZONE, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})

// Accepted transport values: epoch milliseconds, numeric millisecond strings,
// ISO instants with an offset, or Gregorian date-only strings (Tehran days).
// Reject ambiguous local date-times rather than using the device timezone.
export function parseDate(value: DateInput): Temporal.Instant | null {
  try {
    if (value instanceof Date) value = value.getTime()
    if (typeof value === 'number') return Temporal.Instant.fromEpochMilliseconds(value)
    if (typeof value !== 'string' || !value.trim()) return null
    value = value.trim()
    if (/^[+-]?\d+$/.test(value)) return Temporal.Instant.fromEpochMilliseconds(Number(value))
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return Temporal.PlainDate.from(value, { overflow: 'reject' }).toZonedDateTime(CHEF_TIME_ZONE).toInstant()
    }
    return Temporal.Instant.from(value)
  } catch { return null }
}

function dateOf(value: DateInput) {
  const instant = parseDate(value)
  return instant ? new Date(instant.epochMilliseconds) : null
}

function plainDate(value: DateInput) {
  const instant = parseDate(value)
  if (!instant) throw new RangeError('Invalid report date')
  return instant.toZonedDateTimeISO(CHEF_TIME_ZONE).toPlainDate()
}

export function formatPersianDate(value: DateInput, separator = '/', order: 'ymd' | 'dmy' | 'md' = 'ymd') {
  const date = dateOf(value)
  if (!date) return '—'
  const parts = persian.formatToParts(date)
  const get = (type: string) => parts.find(part => part.type === type)?.value ?? ''
  const fields = order === 'dmy' ? ['day', 'month', 'year'] : order === 'md' ? ['month', 'day'] : ['year', 'month', 'day']
  return fields.map(get).join(separator)
}

export function formatWeekday(value: DateInput) {
  const date = dateOf(value)
  return date ? weekday.format(date) : '—'
}

export function formatPersianDayMonth(value: DateInput) {
  const date = dateOf(value)
  if (!date) return { day: '—', month: '—', year: '—', monthYear: '—' }
  const parts = persianDayMonth.formatToParts(date)
  const get = (type: string) => parts.find(part => part.type === type)?.value ?? ''
  return { day: get('day'), month: get('month'), year: get('year'), monthYear: `${get('month')} ${get('year')}` }
}

export function formatPersianMonthDay(value: DateInput) {
  const date = dateOf(value)
  return date ? persianMonthDay.format(date) : '—'
}

export function formatTime(value: DateInput) {
  const date = dateOf(value)
  return date ? time.format(date) : '—'
}

export function getTehranCalendarDate(value: DateInput): CalendarDate {
  const date = plainDate(value)
  return { year: date.year, month: date.month, day: date.day }
}

export function getTehranDayKey(value: DateInput) {
  const instant = parseDate(value)
  return instant ? instant.toZonedDateTimeISO(CHEF_TIME_ZONE).toPlainDate().toString() : null
}

export type InclusiveDateRange = { from: number; to: number }
export type DateRangePreset = 'today' | 'last7' | 'last90'
export type DateRangeSelection = { preset: DateRangePreset | 'custom'; range: InclusiveDateRange }

// Calendar operations remain date-only until the API boundary; the backend's
// inclusive "to" is the next Tehran day start minus one millisecond.
function wholeDays(start: Temporal.PlainDate, end: Temporal.PlainDate): InclusiveDateRange {
  return {
    from: start.toZonedDateTime(CHEF_TIME_ZONE).epochMilliseconds,
    to: end.add({ days: 1 }).toZonedDateTime(CHEF_TIME_ZONE).epochMilliseconds - 1,
  }
}

export function getTehranDayRange(value: DateInput = Date.now()) {
  const day = plainDate(value)
  return wholeDays(day, day)
}

export function getRecentDaysRange(days: number, now = Date.now()): InclusiveDateRange {
  if (!Number.isSafeInteger(days) || days < 1) throw new RangeError('Invalid day count')
  const today = plainDate(now)
  return { from: today.subtract({ days: days - 1 }).toZonedDateTime(CHEF_TIME_ZONE).epochMilliseconds, to: now }
}

export function getCalendarDateRange(start: CalendarDate, end: CalendarDate): InclusiveDateRange | null {
  try {
    const first = Temporal.PlainDate.from(start, { overflow: 'reject' })
    const last = Temporal.PlainDate.from(end, { overflow: 'reject' })
    return Temporal.PlainDate.compare(first, last) <= 0 ? wholeDays(first, last) : null
  } catch { return null }
}

export function getPresetDateSelection(preset: DateRangePreset, now = Date.now()): DateRangeSelection {
  return { preset, range: preset === 'today' ? getTehranDayRange(now) : getRecentDaysRange(preset === 'last7' ? 7 : 90, now) }
}
